import React, { useEffect, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookOpenCheck, BarChart3, ArrowRight, Sparkles, Star } from "lucide-react";

const SUBJECT_LABEL = {
  reading: { en: "Reading", zh: "阅读" },
  math: { en: "Math", zh: "数学" },
  language: { en: "Language Usage", zh: "语言运用" },
};

const SKILL_LABEL_EN = {
  vocab: "Vocabulary", literal: "Literal comprehension", inferential: "Inference",
  main_idea: "Main idea", author_purpose: "Author's purpose", text_structure: "Text structure",
  number_ops: "Number & operations", fractions_decimals: "Fractions & decimals",
  algebra: "Algebra", geometry: "Geometry", data_stats: "Data & statistics",
  grammar: "Grammar", punctuation: "Punctuation", usage: "Usage",
  vocabulary_in_context: "Vocab in context", writing_revision: "Writing revision",
};

function ResultView({ result, testKind, lang, onReset }) {
  const isStar = testKind === "star_reading";
  return (
    <div className="space-y-5" data-testid="mock-result">
      <div className="rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-5">
        <div className="flex items-center gap-3">
          {isStar ? <Star className="w-5 h-5 text-[hsl(var(--primary))]" /> : <BarChart3 className="w-5 h-5 text-[hsl(var(--primary))]" />}
          <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">
            {isStar ? (lang === "zh" ? "STAR 阅读 · 模拟报告" : "STAR Reading · Mock Report") : (lang === "zh" ? "MAP · 模拟报告" : "MAP · Mock Report")}
          </p>
        </div>
        {isStar ? (
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div><p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{lang==="zh"?"等量分数":"Scaled Score"}</p><p className="font-display text-2xl mt-0.5">{result.scaled_score}</p></div>
            <div><p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{lang==="zh"?"年级等量":"Grade Equiv."}</p><p className="font-display text-2xl mt-0.5">GE {result.grade_equivalent}</p></div>
            <div><p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{lang==="zh"?"教学水平":"Instructional"}</p><p className="font-display text-xl mt-0.5">{result.instructional_level}</p></div>
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-3 gap-3">
            {Object.entries(result.rit_by_subject || {}).map(([subj, s]) => (
              <div key={subj}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{SUBJECT_LABEL[subj][lang === "zh" ? "zh" : "en"]} · RIT</p>
                <p className="font-display text-2xl mt-0.5">{s.rit}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {result.strengths?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-emerald-700">{lang==="zh"?"优势 · ":"Strengths · "}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {result.strengths.map(sk => (
              <span key={sk} className="px-2.5 py-1 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{SKILL_LABEL_EN[sk] || sk}</span>
            ))}
          </div>
        </div>
      )}
      {result.weaknesses?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-amber-700">{lang==="zh"?"待加强 · ":"To strengthen · "}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {result.weaknesses.map(sk => (
              <span key={sk} className="px-2.5 py-1 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-200">{SKILL_LABEL_EN[sk] || sk}</span>
            ))}
          </div>
        </div>
      )}
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">{lang==="zh"?"5 条学习建议":"5 Study Suggestions"}</p>
        <ol className="mt-2 space-y-2 list-decimal pl-5 text-sm text-[hsl(var(--foreground))]">
          {(result.suggestions || []).map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">{lang==="zh"?"推荐阅读":"Recommended Reading"}</p>
        <ul className="mt-2 list-disc pl-5 text-sm text-[hsl(var(--foreground))] space-y-1">
          {(result.reading_list || []).map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      </div>
      <Button onClick={onReset} variant="outline" className="rounded-full" data-testid="mock-reset"><ArrowRight className="w-4 h-4 mr-1.5" />{lang==="zh"?"返回":"Back to portal"}</Button>
    </div>
  );
}

export default function MockTestCard() {
  const { lang } = useLang();
  const [phase, setPhase] = useState("idle"); // idle | running | finished
  const [testCode, setTestCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(null);
  const [question, setQuestion] = useState(null);
  const [progress, setProgress] = useState({ answered: 0, total: 20 });
  const [selectedOption, setSelectedOption] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const startTest = async () => {
    const code = testCode.trim().toUpperCase();
    if (!code) { toast.error(lang === "zh" ? "请输入考试码" : "Enter your test code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mock/start", { code });
      if (data.result) {
        setLastResult({ ...data.result, _test_kind: data.attempt.test_kind });
        setPhase("finished");
        return;
      }
      setAttempt(data.attempt);
      setQuestion(data.next_question);
      setProgress(data.progress || { answered: 0, total: 20 });
      setSelectedOption(null);
      setPhase("running");
    } catch (err) { toast.error(getErrorDetail(err)); }
    finally { setBusy(false); }
  };

  async function submitAnswer() {
    if (selectedOption === null) { toast.error(lang === "zh" ? "请选择答案" : "Pick an option first"); return; }
    if (!question || !attempt) return;
    setBusy(true);
    try {
      const { data } = await api.post("/mock/answer", { attempt_id: attempt.id, question_id: question.id, answer_index: selectedOption });
      if (data.result) {
        setLastResult({ ...data.result, _test_kind: attempt.test_kind });
        setPhase("finished");
        setQuestion(null);
        return;
      }
      setQuestion(data.next_question);
      setProgress(data.progress);
      setSelectedOption(null);
    } catch (err) { toast.error(getErrorDetail(err)); }
    finally { setBusy(false); }
  }

  function reset() {
    setPhase("idle"); setTestCode(""); setAttempt(null); setQuestion(null);
    setSelectedOption(null); setLastResult(null);
    setProgress({ answered: 0, total: 20 });
  }

  return (
    <div data-testid="mock-test-card" className="rounded-2xl border border-[hsl(var(--border))] bg-white p-6 md:p-8">
      <div className="flex items-center gap-3">
        <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" />
        <h3 className="font-display text-xl md:text-2xl">{lang === "zh" ? "STAR 阅读 / MAP 模拟测试" : "STAR Reading / MAP Mock Test"}</h3>
      </div>
      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
        {lang === "zh"
          ? "20 题自适应模拟测试 — 涵盖 STAR 阅读 或 MAP 阅读 + 数学 + 语言。完成后可获得分数与 5 条针对性学习建议。"
          : "A 20-question adaptive mock test — either STAR Reading or MAP (Reading + Math + Language). You'll get a personalised score and 5 study suggestions when you finish."}
      </p>

      {phase === "idle" && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end" data-testid="mock-idle">
          <div className="sm:col-span-7">
            <Label className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "考试码" : "Test code"}</Label>
            <Input placeholder="e.g. K9X4-LM7P" value={testCode} onChange={(e)=>setTestCode(e.target.value)} data-testid="mock-code-input" className="mt-2" />
          </div>
          <div className="sm:col-span-5">
            <Button onClick={startTest} disabled={busy} className="w-full rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="mock-start-btn">
              <BookOpenCheck className="w-4 h-4 mr-1.5" />{busy ? "…" : (lang === "zh" ? "开始测试" : "Start test")}
            </Button>
          </div>
          <p className="sm:col-span-12 text-xs text-[hsl(var(--muted-foreground))] italic">
            {lang === "zh" ? "没有考试码？请向您的老师索取。" : "No code? Ask your teacher to issue one in the admin panel."}
          </p>
        </div>
      )}

      {phase === "running" && question && (
        <div className="mt-6 space-y-4" data-testid="mock-running">
          <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
            <span data-testid="mock-progress">{progress.answered}/{progress.total} {lang === "zh" ? "题" : "questions"}</span>
            <span>{progress.current_subject ? SUBJECT_LABEL[progress.current_subject]?.[lang === "zh" ? "zh" : "en"] : ""} · {lang === "zh" ? "难度" : "difficulty"} {progress.current_difficulty}/5</span>
          </div>
          <div className="w-full h-1 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
            <div className="h-full bg-[hsl(var(--primary))] transition-all" style={{ width: `${(progress.answered / progress.total) * 100}%` }} />
          </div>
          {question.passage && (
            <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-4 text-sm italic text-[hsl(var(--foreground))] whitespace-pre-wrap" data-testid="mock-passage">{question.passage}</div>
          )}
          <p className="font-display text-lg" data-testid="mock-prompt">{question.prompt}</p>
          <div className="grid grid-cols-1 gap-2">
            {question.options.map((opt, i) => (
              <button key={i} type="button" onClick={()=>setSelectedOption(i)} data-testid={`mock-opt-${i}`}
                className={`text-left rounded-xl border px-4 py-3 transition-all ${selectedOption === i ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 ring-2 ring-[hsl(var(--primary))]/20" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/40 bg-white"}`}>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mr-2">{String.fromCharCode(65 + i)}</span>
                {opt}
              </button>
            ))}
          </div>
          <Button onClick={submitAnswer} disabled={busy || selectedOption === null} className="rounded-full bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/90 text-[hsl(var(--background))]" data-testid="mock-submit">
            {busy ? "…" : (lang === "zh" ? "下一题" : "Next")} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      )}

      {phase === "finished" && lastResult && (
        <div className="mt-6">
          <ResultView result={lastResult} testKind={lastResult._test_kind} lang={lang} onReset={reset} />
        </div>
      )}
    </div>
  );
}
